/**
 * P0.5.13A — Team Match Exposure Section
 *
 * Consumes canonical endpoints:
 *   GET /reports/match-exposure/team/{team_id}?date_from&date_to
 *   GET /reports/match-exposure/team/{team_id}/timeseries?date_from&date_to
 *
 * Strict semantics:
 *   • NO frontend formula duplication
 *   • null vs 0 never conflated
 *   • Client-side event_type chart filter never mutates summary KPIs
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Users, AlertTriangle, Loader2, Info } from 'lucide-react';

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const fmtNum = (v, suffix = '') => (isNum(v) ? `${v}${suffix}` : '—');
const fmtPct = (v) => (isNum(v) ? `${v}%` : '—');

const tooltipStyle = {
  backgroundColor: '#0f172a',
  border: '1px solid #334155',
  borderRadius: '6px',
  fontSize: '11px',
  color: '#e2e8f0',
};

const KpiCard = ({ label, value, hint, testid }) => (
  <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
      {label}
    </div>
    <div className="text-2xl font-bold text-cyan-300">{value}</div>
    {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
  </Card>
);

const EventTypeTabs = ({ value, onChange, t }) => {
  const opts = [
    { v: 'ALL', label: t('All', 'Tutte') },
    { v: 'MATCH', label: t('Competitive', 'Competizione') },
    { v: 'FRIENDLY_MATCH', label: t('Friendly', 'Amichevoli') },
  ];
  return (
    <div className="flex items-center gap-1" role="tablist" aria-label={t('Match type filter', 'Filtro tipo partita')}>
      {opts.map((o) => (
        <button
          key={o.v}
          type="button"
          role="tab"
          aria-selected={value === o.v}
          onClick={() => onChange(o.v)}
          data-testid={`team-me-filter-${o.v}`}
          className={`px-2.5 py-1 rounded text-[11px] font-medium border transition-colors min-h-[28px] ${
            value === o.v
              ? 'bg-cyan-600 border-cyan-500 text-white'
              : 'bg-zinc-950 border-zinc-800 text-zinc-400 hover:text-zinc-200'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
};

const ChartTabs = ({ tabs, value, onChange }) => (
  <div className="flex flex-wrap items-center gap-1 border-b border-zinc-800 mb-3" role="tablist">
    {tabs.map((tab) => (
      <button
        key={tab.v}
        type="button"
        role="tab"
        aria-selected={value === tab.v}
        onClick={() => onChange(tab.v)}
        data-testid={`team-me-chart-${tab.v}`}
        className={`px-3 py-2 text-xs font-medium border-b-2 min-h-[36px] transition-colors ${
          value === tab.v
            ? 'border-cyan-500 text-cyan-300'
            : 'border-transparent text-zinc-500 hover:text-zinc-300'
        }`}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

const TeamMatchTooltip = ({ active, payload, t }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  const typeLabel = p.event_type === 'FRIENDLY_MATCH'
    ? t('Friendly', 'Amichevole')
    : t('Competitive', 'Competizione');
  return (
    <div style={tooltipStyle} className="p-2 space-y-0.5">
      <div className="text-zinc-100 font-semibold">{p.title || p.date}</div>
      <div className="text-zinc-400">
        {p.date} · <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-300">{typeLabel}</Badge>
      </div>
      <div className="text-zinc-300">
        {t('Player-Minutes', 'Player-Minutes')}:{' '}
        <b className="text-cyan-300">{fmtNum(p.player_minutes)}</b>{' '}
        <span className="text-[10px] text-zinc-500">
          ({p.known_athlete_rows} {t('entered', 'inseriti')}
          {p.missing_exposure_entries ? ` · ${p.missing_exposure_entries} ${t('missing', 'mancanti')}` : ''})
        </span>
      </div>
      <div className="text-zinc-300">
        {t('Avg Player Minutes', 'Media minuti giocatore')}: <b className="text-zinc-200">{fmtNum(p.average_player_minutes)}</b>
      </div>
      <div className="text-zinc-300">
        {t('Avg RPE', 'RPE medio')}: <b className="text-zinc-200">{fmtNum(p.average_rpe)}</b>{' '}
        <span className="text-[10px] text-zinc-500">({p.rpe_entries} {t('entered', 'inseriti')})</span>
      </div>
      <div className="text-zinc-300">
        {t('Match-Day Load', 'Match-Day Load')}: <b className="text-zinc-200">{isNum(p.match_day_training_load) ? `${p.match_day_training_load} AU` : '—'}</b>
      </div>
      <div className="text-zinc-300">
        {t('Available Minutes', 'Minuti disponibili')}: <b className="text-zinc-200">{fmtNum(p.match_available_minutes)}</b>
      </div>
    </div>
  );
};

const TeamMatchExposureSection = ({ api, teamId, dateFrom, dateTo, t }) => {
  const [aggregate, setAggregate] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chart, setChart] = useState('player_minutes');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState({ key: 'athlete_name', dir: 'asc' });

  useEffect(() => {
    if (!teamId || !dateFrom || !dateTo) return;
    setLoading(true);
    setError(null);
    setAggregate(null);
    setTimeseries(null);

    const params = { date_from: dateFrom, date_to: dateTo };

    Promise.all([
      api.get(`/reports/match-exposure/team/${teamId}`, { params }),
      api.get(`/reports/match-exposure/team/${teamId}/timeseries`, { params }),
    ])
      .then(([a, ts]) => {
        setAggregate(a.data || {});
        setTimeseries(ts.data || { series: [] });
      })
      .catch((e) => setError(e?.response?.data?.detail || t('Failed to load Match Exposure', 'Caricamento Match Exposure fallito')))
      .finally(() => setLoading(false));
  }, [teamId, dateFrom, dateTo, api, t]);

  const filteredSeries = useMemo(() => {
    if (!timeseries?.series) return [];
    if (typeFilter === 'ALL') return timeseries.series;
    return timeseries.series.filter((r) => r.event_type === typeFilter);
  }, [timeseries, typeFilter]);

  const sortedAthletes = useMemo(() => {
    if (!aggregate?.athletes) return [];
    const rows = [...aggregate.athletes];
    const dir = sortBy.dir === 'asc' ? 1 : -1;
    const cmp = (a, b) => {
      const va = a[sortBy.key];
      const vb = b[sortBy.key];
      // null last
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    };
    rows.sort(cmp);
    return rows;
  }, [aggregate, sortBy]);

  const handleSort = (key) => {
    setSortBy((s) => ({
      key,
      dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="team-match-exposure-loading">
        <div className="flex items-center gap-2 text-zinc-400 text-sm py-6 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('Loading…', 'Caricamento…')}
        </div>
      </Card>
    );
  }
  if (error) {
    return (
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="team-match-exposure-error">
        <div className="text-sm text-red-400 py-3">{error}</div>
      </Card>
    );
  }
  if (!aggregate) return null;

  const isEmpty = (aggregate.matches ?? 0) === 0;
  const missing = aggregate.missing_exposure_entries ?? 0;

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="team-match-exposure-section">
      <div className="flex items-start gap-3 mb-4">
        <Users className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-zinc-100">
            {t('Match Exposure', 'Match Exposure')}
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            {t(
              'Actual playing time and match-day exposure across the squad.',
              'Minutaggio effettivo e carico partita distribuito nella rosa.',
            )}
          </p>
        </div>
      </div>

      {isEmpty ? (
        <div
          className="text-xs text-zinc-500 italic py-6 text-center"
          data-testid="team-match-exposure-empty"
        >
          {t(
            'No Match Exposure data available for the selected period.',
            'Nessun dato Match Exposure disponibile nel periodo selezionato.',
          )}
        </div>
      ) : (
        <>
          {missing > 0 && (
            <div
              className="bg-amber-950/30 border border-amber-500/40 rounded p-2.5 mb-4 text-xs text-amber-200 flex items-start gap-2"
              data-testid="team-match-exposure-incomplete"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <b>
                  {t('Incomplete Match Exposure Data', 'Dati di minutaggio incompleti')}
                </b>
                {': '}
                {t(
                  `${missing} missing entries.`,
                  `${missing} rilevazioni mancanti.`,
                )}
              </div>
            </div>
          )}

          {/* Primary KPI cards */}
          <div
            className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3"
            data-testid="team-match-exposure-kpis-primary"
          >
            <KpiCard
              label={t('Matches', 'Partite')}
              value={fmtNum(aggregate.matches)}
              testid="team-me-kpi-matches"
            />
            <KpiCard
              label={t('Total Player-Minutes', 'Player-Minutes totali')}
              value={fmtNum(aggregate.total_player_minutes)}
              testid="team-me-kpi-player-min"
              hint={
                aggregate.known_athlete_match_rows != null
                  ? `${aggregate.known_athlete_match_rows} ${t('entries', 'rilevazioni')}`
                  : null
              }
            />
            <KpiCard
              label={t('Average Player Minutes', 'Media minuti giocatore')}
              value={fmtNum(aggregate.average_player_minutes)}
              testid="team-me-kpi-avg-min"
              hint={t('per known entry', 'per rilevazione nota')}
            />
            <KpiCard
              label={t('Average Match RPE', 'RPE medio partita')}
              value={fmtNum(aggregate.average_rpe)}
              testid="team-me-kpi-rpe"
              hint={
                isNum(aggregate.average_rpe)
                  ? `${aggregate.rpe_entries} ${t('entries', 'rilevazioni')}`
                  : null
              }
            />
            <KpiCard
              label={t('Total Match-Day Load', 'Match-Day Load totale')}
              value={isNum(aggregate.total_match_day_training_load) ? `${aggregate.total_match_day_training_load} AU` : '—'}
              testid="team-me-kpi-load"
            />
            <KpiCard
              label={t('Total Available Match Minutes', 'Minuti disponibili totali')}
              value={fmtNum(aggregate.total_available_match_minutes)}
              testid="team-me-kpi-available"
              hint={t('event-level', 'a livello evento')}
            />
          </div>

          {/* Secondary compact strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-5">
            <div className="text-zinc-500">
              {t('Post-Match Minutes', 'Minuti post partita')}:{' '}
              <span className="text-zinc-200 font-medium">{fmtNum(aggregate.total_post_match_minutes)}</span>
            </div>
            {missing > 0 && (
              <div className="text-amber-400" data-testid="team-me-missing-badge">
                {t('Missing Data', 'Dati mancanti')}: {missing}
              </div>
            )}
          </div>

          {/* Chart selector + type filter */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <ChartTabs
              value={chart}
              onChange={setChart}
              tabs={[
                { v: 'player_minutes', label: t('Player-Minutes', 'Player-Minutes') },
                { v: 'avg_minutes', label: t('Avg Player Minutes', 'Media min. giocatore') },
                { v: 'rpe', label: t('Avg RPE', 'RPE medio') },
                { v: 'load', label: t('Match-Day Load', 'Match-Day Load') },
              ]}
            />
            <EventTypeTabs value={typeFilter} onChange={setTypeFilter} t={t} />
          </div>

          {typeFilter !== 'ALL' && (
            <div
              className="text-[11px] text-zinc-500 italic flex items-center gap-1 mb-2"
              data-testid="team-me-filter-notice"
            >
              <Info className="w-3 h-3" />
              {t(
                'Chart filtered. Summary KPI cards still reflect the full selected period.',
                'Grafico filtrato. Le KPI riepilogative restano sull\'intero periodo selezionato.',
              )}
            </div>
          )}

          {/* Chart body */}
          <div data-testid="team-match-exposure-chart">
            {filteredSeries.length === 0 ? (
              <div className="text-xs text-zinc-500 italic py-6 text-center">
                {t('No matches in current filter.', 'Nessuna partita per il filtro selezionato.')}
              </div>
            ) : (
              <TeamChartBody chart={chart} data={filteredSeries} t={t} />
            )}
          </div>

          {/* Athlete table */}
          <div className="mt-6" data-testid="team-match-exposure-table-wrap">
            <div className="text-xs uppercase text-zinc-500 mb-2">
              {t('Athletes — Match Exposure', 'Atleti — Match Exposure')}
            </div>
            <AthleteTable
              rows={sortedAthletes}
              sortBy={sortBy}
              onSort={handleSort}
              t={t}
            />
          </div>
        </>
      )}
    </Card>
  );
};

/* ─── Athlete Table ─────────────────────────────────────────── */
const AthleteTable = ({ rows, sortBy, onSort, t }) => {
  const cols = [
    { key: 'athlete_name', label: t('Athlete', 'Atleta'), align: 'left' },
    { key: 'minutes_played', label: t('Minutes Played', 'Min. giocati'), align: 'right' },
    { key: 'total_available_match_minutes', label: t('Available', 'Disp.'), align: 'right' },
    { key: 'playing_time_pct', label: t('Playing Time', 'Playing Time'), align: 'right', pct: true },
    { key: 'appearances', label: t('Appearances', 'Presenze'), align: 'right' },
    { key: 'avg_minutes_per_appearance', label: t('Avg / App', 'Media/pres.'), align: 'right' },
    { key: 'avg_rpe', label: t('Avg RPE', 'RPE medio'), align: 'right' },
    { key: 'match_day_training_load', label: t('Match-Day Load', 'Load'), align: 'right' },
    { key: 'post_match_minutes', label: t('Post-Match', 'Post partita'), align: 'right' },
    { key: 'missing_exposure_entries', label: t('Missing', 'Mancanti'), align: 'right' },
  ];
  const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
  const fmt = (v, opts = {}) => {
    if (!isNum(v)) return '—';
    if (opts.pct) return `${v}%`;
    return `${v}`;
  };
  return (
    <div className="overflow-x-auto" data-testid="team-match-exposure-athlete-table">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] uppercase text-zinc-500 border-b border-zinc-800">
            {cols.map((c) => (
              <th
                key={c.key}
                className={`py-2 px-2 ${c.align === 'right' ? 'text-right' : 'text-left'} cursor-pointer select-none hover:text-zinc-300`}
                onClick={() => onSort(c.key)}
                data-testid={`team-me-th-${c.key}`}
              >
                {c.label}
                {sortBy.key === c.key ? (sortBy.dir === 'asc' ? ' ▲' : ' ▼') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.athlete_id}
              className="border-b border-zinc-800/60 last:border-0"
              data-testid={`team-me-row-${r.athlete_id}`}
            >
              {cols.map((c) => {
                const val = c.pct ? fmt(r[c.key], { pct: true }) : fmt(r[c.key]);
                const highlight = c.key === 'missing_exposure_entries' && r.missing_exposure_entries > 0
                  ? 'text-amber-400 font-semibold'
                  : c.key === 'athlete_name'
                    ? 'text-zinc-100'
                    : 'text-zinc-200 tabular-nums';
                return (
                  <td
                    key={c.key}
                    className={`py-2 px-2 ${c.align === 'right' ? 'text-right' : 'text-left'} ${highlight}`}
                  >
                    {c.key === 'athlete_name' ? (r.athlete_name || r.athlete_id.slice(0, 8)) : val}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ─── Team Chart bodies ─────────────────────────────────────── */
const TeamChartBody = ({ chart, data, t }) => {
  const rows = data.map((r) => ({
    ...r,
    label: r.date?.slice(5) || r.event_id.slice(0, 6),
  }));

  if (chart === 'player_minutes') {
    return (
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
          <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
          <Tooltip content={<TeamMatchTooltip t={t} />} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar
            dataKey="player_minutes"
            name={t('Player-Minutes', 'Player-Minutes')}
            fill="#1E6BFF"
          />
        </BarChart>
      </ResponsiveContainer>
    );
  }
  if (chart === 'avg_minutes') {
    return (
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
          <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
          <Tooltip content={<TeamMatchTooltip t={t} />} />
          <Line
            type="linear"
            dataKey="average_player_minutes"
            name={t('Average Player Minutes', 'Media minuti giocatore')}
            stroke="#22C55E"
            strokeWidth={2}
            connectNulls={false}
            dot={{ r: 3 }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  }
  if (chart === 'rpe') {
    return (
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
          <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis domain={[0, 10]} tick={{ fill: '#94a3b8', fontSize: 10 }} />
          <Tooltip content={<TeamMatchTooltip t={t} />} />
          <Line
            type="linear"
            dataKey="average_rpe"
            name={t('Avg RPE', 'RPE medio')}
            stroke="#8B5CF6"
            strokeWidth={2}
            connectNulls={false}
            dot={{ r: 3 }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  }
  // load
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
        <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
        <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 10 }} interval="preserveStartEnd" />
        <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
        <Tooltip content={<TeamMatchTooltip t={t} />} />
        <Bar
          dataKey="match_day_training_load"
          name={t('Team Match-Day Load (AU)', 'Team Match-Day Load (AU)')}
          fill="#06B6D4"
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default TeamMatchExposureSection;
export { TeamMatchExposureSection };
